import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Plus,
  RefreshCw,
  AlertCircle,
  Loader2,
  UserPlus,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'react-toastify';
import AuthNavbar from '../../components/navigations/AuthNavbar';
import clientService from '../../api/ClientService';
import { mapClientData, filterClients, calculateStats } from '../../utils/clientHelpers';
import ClientStatsCards from '../../components/agents/clients/ClientStatsCards';
import ClientTableRow from '../../components/agents/clients/ClientTableRow';
import ClientPagination from '../../components/agents/clients/ClientPagination';
import ClientViewModal from '../../components/agents/clients/ClientViewModal';
import VerifyClientModal from '../../components/agents/clients/VerifyClientModal';
import LoadingSkeleton from '../../components/agents/clients/LoadingSkeleton';

export default function OthersClients() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [clientToVerify, setClientToVerify] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Fetch clients from API
  const fetchClients = async (showRefreshing = false) => {
    if (showRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await clientService.getClients();
      
      if (response.data.status_code === 200 && Array.isArray(response.data.data)) {
        setClients(response.data.data);
        if (showRefreshing) {
          toast.success('Clients refreshed successfully');
        }
      } else {
        setError(response.data.message || 'Failed to fetch clients');
        toast.error(response.data.message || 'Failed to fetch clients');
      }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Memoized mapped clients
  const mappedClients = useMemo(() => {
    return clients.map(mapClientData);
  }, [clients]);

  // Memoized filtered clients
  const filteredClients = useMemo(() => {
    return filterClients(mappedClients, searchTerm, selectedFilter);
  }, [mappedClients, searchTerm, selectedFilter]);

  // Memoized stats
  const stats = useMemo(() => {
    return calculateStats(mappedClients);
  }, [mappedClients]);

  // Pagination
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentClients = filteredClients.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setViewModalOpen(true);
  };

  const handleEditClient = (client) => {
    navigate(`/edit-client/${client.slug}`, { state: { client } });
  };

  const handleVerifyClient = (client) => {
    setClientToVerify(client);
    setVerifyModalOpen(true);
  };

  const handleCloseVerifyModal = () => {
    setVerifyModalOpen(false);
    setClientToVerify(null);
  };

  const handleConfirmVerify = async () => {
    if (!clientToVerify) return;

    setIsVerifying(true);
    try {
      const isCurrentlyVerified = clientToVerify.status?.toLowerCase() === 'active' || 
                                   clientToVerify.emailVerified || 
                                   clientToVerify.phoneVerified;
      const response = await clientService.verifyRegisteredClient(clientToVerify.slug);

      if (response.status) {
        // API toggles status but doesn't return updated client; refetch list after.
        toast.success(response.message || (isCurrentlyVerified ? 'Client unverified successfully' : 'Client verified successfully'));
        setVerifyModalOpen(false);
        setClientToVerify(null);
        // Refresh the clients list
        fetchClients(true);
      } else {
        toast.error(response.message || 'Failed to update verification status');
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
      console.error('Verification error:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedClient(null);
  };

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedFilter]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50">
      <AuthNavbar />
      
      <div className="px-4 pt-16 pb-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-600 rounded-lg transition-all duration-200 hover:text-emerald-600 hover:bg-emerald-50"
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-6 h-6" />
              </motion.button>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Clients
                </h1>
                <p className="mt-2 text-lg text-gray-600">
                  Manage and track all your registered non-student clients
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => fetchClients(true)}
                disabled={refreshing}
                className="inline-flex items-center px-4 py-3 text-sm font-semibold text-gray-700 bg-white rounded-xl border-2 border-gray-200 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`mr-2 w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/new-registration')}
                className="inline-flex items-center px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl shadow-lg transition-all duration-200 hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                <Plus className="mr-2 w-5 h-5" />
                Add New Client
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <ClientStatsCards stats={stats} loading={loading} />

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by name, email, phone, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="py-3 pr-4 pl-10 w-full text-sm rounded-xl border-2 border-gray-200 bg-white transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            <div className="flex gap-3">
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="px-4 py-3 text-sm rounded-xl border-2 border-gray-200 bg-white transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Error State */}
        {error && !loading && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 p-6 bg-red-50 border-2 border-red-200 rounded-xl"
          >
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
              <div>
                <p className="text-sm font-semibold text-red-900">Error loading clients</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button
                onClick={() => fetchClients()}
                className="ml-auto px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
              >
                Retry
              </button>
            </div>
          </motion.div>
        )}

        {/* Loading State */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="overflow-hidden bg-white rounded-2xl border-2 border-gray-200 shadow-lg"
          >
            <div className="p-8">
              <div className="flex items-center justify-center mb-6">
                <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                <span className="ml-3 text-gray-600">Loading clients...</span>
              </div>
              <LoadingSkeleton />
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredClients.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overflow-hidden bg-white rounded-2xl border-2 border-gray-200 shadow-lg"
          >
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                <UserPlus className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchTerm || selectedFilter !== 'all' ? 'No clients found' : 'No clients yet'}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Get started by registering your first client'}
              </p>
              {(!searchTerm && selectedFilter === 'all') && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/new-registration')}
                  className="inline-flex items-center px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl shadow-lg hover:from-emerald-700 hover:to-teal-700 transition-all"
                >
                  <Plus className="mr-2 w-5 h-5" />
                  Add Your First Client
                </motion.button>
              )}
            </div>
          </motion.div>
        )}

        {/* Table */}
        {!loading && !error && filteredClients.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="overflow-hidden bg-white rounded-2xl border-2 border-gray-200 shadow-lg"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                      Client
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                      Location
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                      ID Information
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  <AnimatePresence>
                    {currentClients.map((client, index) => (
                      <ClientTableRow
                        key={client.id}
                        client={client}
                        index={index}
                        onView={handleViewClient}
                        onEdit={handleEditClient}
                        onVerify={handleVerifyClient}
                      />
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Pagination */}
        {!loading && !error && filteredClients.length > 0 && (
          <ClientPagination
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            totalItems={filteredClients.length}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* View Client Details Modal */}
      <ClientViewModal
        isOpen={viewModalOpen}
        client={selectedClient}
        onClose={handleCloseModal}
      />

      {/* Verify Client Modal */}
      <VerifyClientModal
        isOpen={verifyModalOpen}
        client={clientToVerify}
        isVerified={clientToVerify ? (clientToVerify.status?.toLowerCase() === 'active' || clientToVerify.emailVerified || clientToVerify.phoneVerified) : false}
        onClose={handleCloseVerifyModal}
        onConfirm={handleConfirmVerify}
        isProcessing={isVerifying}
      />
    </div>
  );
}
